<app-menu>
  <div class="cfr-shell" [attr.data-theme]="theme">
    <div class="mx-auto flex max-w-[1800px] flex-col gap-4 p-4 lg:p-6">
      <header class="cfr-panel flex flex-wrap items-center justify-between gap-4 p-5">
        <div>
          <div class="cfr-eyebrow">Financials / Customer statements</div>
          <h1 class="m-0 mt-1 text-2xl font-bold">Customer financial reports</h1>
          <p class="cfr-muted m-0 mt-2 text-sm">Select ladings, review their charges, and download a customer statement.</p>
        </div>
        <div class="flex flex-wrap gap-2">
          <button class="cfr-button" type="button" (click)="toggleTheme()">
            <i class="fa-solid" [ngClass]="theme === 'dark' ? 'fa-sun' : 'fa-moon'" aria-hidden="true"></i>
            {{ theme === 'dark' ? 'Light' : 'Dark' }}
          </button>
          <button class="cfr-button" type="button" (click)="loadData()" [disabled]="busy || loading">
            <i class="fa-solid fa-rotate" [class.fa-spin]="loading" aria-hidden="true"></i> Refresh
          </button>
        </div>
      </header>

      <section class="cfr-panel grid gap-4 p-5 md:grid-cols-2 xl:grid-cols-3" aria-label="Report filters">
        <div>
          <label class="cfr-label">Customer</label>
          <app-contact-autocomplete [formControl]="customerControl" [panelTheme]="theme" placeholder="Find a customer…"
            [type]="'customer_contact_list'" (selected)="onCustomerSelected($event)"></app-contact-autocomplete>
          <p class="cfr-muted m-0 text-xs">Showing {{ customerName }}</p>
          <div *ngIf="billingAccounts.length" class="mt-3">
            <label class="cfr-label" for="report-billing-account">Billing account</label>
            <select id="report-billing-account" class="cfr-input w-full" [(ngModel)]="billingAccount" (ngModelChange)="billingChanged()" [disabled]="busy">
              <option *ngFor="let account of billingAccounts" [value]="account.billing_account_uuid">{{ account.label }}</option>
            </select>
          </div>
        </div>
        <form (ngSubmit)="loadData(true)" class="flex flex-col justify-center gap-2">
          <label class="cfr-label" for="cfr-search">Find ladings</label>
          <div class="flex gap-2">
            <input id="cfr-search" name="search" class="cfr-input min-w-0 flex-1" [(ngModel)]="search" [disabled]="busy"
              placeholder="Reference, customer PO or consignee" maxlength="100" />
            <button class="cfr-button" type="submit" [disabled]="busy || loading">Search</button>
          </div>
        </form>
        <div class="flex items-center justify-between gap-4 rounded-lg border border-[var(--border)] bg-[var(--surface)] p-4">
          <div><div class="cfr-muted text-xs">Selected for this report</div><div class="mt-1 text-3xl font-bold tabular-nums">{{ selected.size }} <span class="cfr-muted text-sm font-normal">/ 100 ladings</span></div></div>
          <button class="cfr-button" type="button" (click)="clearSelection()" [disabled]="busy || !selected.size">Clear</button>
        </div>
      </section>

      <section class="cfr-panel p-5" aria-label="Paste lading list">
        <label for="cfr-paste" class="mb-2 block text-base font-semibold">Paste a lading list</label>
        <p id="cfr-paste-help" class="cfr-muted mb-3 text-sm">Paste up to 100 lading UUIDs or alternate references for {{ customerName }}. You can mix both. Separate them with lines, commas, semicolons or spreadsheet tabs.</p>
        <textarea id="cfr-paste" class="cfr-input block w-full" rows="3" maxlength="20000" [(ngModel)]="pasteText"
          (ngModelChange)="resetPasteFeedback()" [disabled]="busy" aria-describedby="cfr-paste-help"
          placeholder="Paste lading UUIDs or alternate references here…"></textarea>
        <div class="mt-3 flex flex-wrap items-center justify-between gap-3">
          <p class="cfr-muted m-0 text-xs">Adds exact matches across all pages to your current selection. Duplicate references need a UUID.</p>
          <button class="cfr-button cfr-primary" type="button" (click)="selectPasted()" [disabled]="busy || !pasteText.trim()">
            {{ resolving ? 'Finding ladings…' : 'Find & select pasted ladings' }}
          </button>
        </div>
        <div *ngIf="pasteSummary" class="cfr-success mt-3 text-sm" role="status">{{ pasteSummary }}</div>
        <div *ngIf="pasteIssues.length" class="cfr-notice mt-3 rounded-lg p-3 text-sm" role="status">
          <p class="m-0 font-semibold">These entries were not selected:</p>
          <ul class="mb-0 mt-2 max-h-48 overflow-auto pl-5"><li *ngFor="let issue of pasteIssues"><strong>{{ issue.identifier }}</strong>: {{ issue.reason }}</li></ul>
        </div>
      </section>

      <div *ngIf="error" class="cfr-error cfr-panel p-4" role="alert">{{ error }}</div>
      <div *ngIf="success" class="cfr-success cfr-panel flex flex-wrap items-center justify-between gap-3 p-4" role="status">
        <span>{{ success }}</span><button type="button" class="cfr-button" (click)="downloadAgain()">Download again</button>
      </div>

      <section class="cfr-panel overflow-hidden" aria-label="Available ladings">
        <div class="flex flex-wrap items-center justify-between gap-3 p-4">
          <div><h2 class="m-0 text-base font-semibold">Ladings for {{ customerName }}</h2>
            <p class="cfr-muted m-0 mt-1 text-xs">Incomplete financials remain visible for review. Selection is kept when you change pages.</p></div>
          <div class="flex flex-wrap gap-2">
            <button class="cfr-button" type="button" (click)="selectedOnly = !selectedOnly" [attr.aria-pressed]="selectedOnly" [disabled]="busy">
              {{ selectedOnly ? 'Show all ladings' : 'Selected only (' + selected.size + ')' }}
            </button>
            <button *ngIf="!selectedOnly" class="cfr-button" type="button" (click)="selectVisible()" [disabled]="busy || loading || selected.size >= 100">Select visible eligible ladings</button>
          </div>
        </div>
        <div *ngIf="loading" class="cfr-muted px-4 pb-3" role="status">Loading ladings…</div>
        <ag-grid-angular class="cfr-grid ag-theme-ulaap" [columnDefs]="columnDefs" [defaultColDef]="defaultColDef"
          [rowData]="selectedOnly ? selectedRows : rowData" [gridOptions]="gridOptions" (gridReady)="gridReady($event)"
          [overlayNoRowsTemplate]="selectedOnly ? '<span>No ladings selected.</span>' : '<span>No ladings match this customer and search.</span>'"></ag-grid-angular>
        <div class="flex flex-wrap items-center justify-between gap-3 p-4">
          <span *ngIf="!selectedOnly" class="cfr-muted text-sm">{{ rowData.length }} ladings on page {{ offset / 100 + 1 }} · {{ selected.size }} selected</span>
          <span *ngIf="selectedOnly" class="cfr-muted text-sm">Showing {{ selected.size }} selected ladings across all pages</span>
          <div *ngIf="!selectedOnly" class="flex gap-2"><button class="cfr-button" type="button" (click)="page(-1)" [disabled]="busy || loading || offset === 0">Previous</button>
            <button class="cfr-button" type="button" (click)="page(1)" [disabled]="busy || loading || !hasMore">Next</button></div>
        </div>
      </section>

      <div class="flex flex-wrap items-center justify-between gap-3">
        <p class="cfr-muted m-0 text-sm">Preview is read-only. Creating a report records a statement and downloads its CSV.</p>
        <button type="button" class="cfr-button cfr-primary" (click)="previewSelection()" [disabled]="busy || !selected.size || hasDownload">
          <i class="fa-solid fa-file-lines" aria-hidden="true"></i>{{ previewing ? 'Checking selected ladings…' : 'Preview selected ladings' }}
        </button>
      </div>

      <section *ngIf="preview as report" class="cfr-panel overflow-hidden" aria-label="Report preview">
        <div class="flex flex-wrap items-center justify-between gap-3 p-5">
          <div><div class="cfr-eyebrow">Review before creating</div><h2 class="m-0 mt-1 text-lg font-bold">{{ report.shipment_count }} ladings · {{ report.row_count }} report rows</h2></div>
          <button *ngIf="!hasDownload" class="cfr-button cfr-primary" type="button" (click)="createReport()" [disabled]="busy || !report.can_generate">
            {{ creating ? 'Creating statement…' : 'Create statement & download CSV' }}
          </button>
        </div>
        <p *ngIf="report.unaudited_count || report.previously_exported_count" class="cfr-notice mx-5 mb-4 rounded-lg p-3 text-sm">
          {{ report.unaudited_count }} unaudited · {{ report.previously_exported_count }} previously exported. These are included under the existing report rules.
        </p>
        <div *ngIf="report.excluded.length" class="cfr-error mx-5 mb-4" role="alert">
          <p>Some selected ladings are unavailable. Adjust the selection and preview again.</p>
          <ul><li *ngFor="let excluded of report.excluded">{{ excluded.alternate_reference || excluded.lading_uuid }}: {{ excluded.reason }}</li></ul>
        </div>
        <ag-grid-angular class="cfr-preview-grid ag-theme-ulaap" [columnDefs]="previewColumns" [defaultColDef]="defaultColDef" [rowData]="report.data"></ag-grid-angular>
      </section>
    </div>
  </div>
</app-menu>
